import React, { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { 
  Users, 
  DollarSign, 
  Activity, 
  TrendingUp,
  Shield,
  Database,
  Server,
  AlertTriangle
} from 'lucide-react'
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LineChart, Line, PieChart, Pie, Cell } from 'recharts'
import { useAuth } from '../contexts/AuthContext'
import { SupabaseService } from '../lib/supabase'
import toast from 'react-hot-toast'

const AdminDashboard = () => {
  const { profile } = useAuth()
  const [stats, setStats] = useState({
    totalUsers: 0,
    totalRevenue: 0,
    totalTransactions: 0,
    systemHealth: 99.9,
    activeUsers: 0,
    newUsersToday: 0
  })
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    loadAdminStats()
  }, [])

  const loadAdminStats = async () => {
    if (!user?.id) return

    try {
      setLoading(true)
      
      // Charger les vraies statistiques depuis Supabase
      const { data: allProfiles, error: profilesError } = await supabase
        .from('profiles')
        .select('*')
      
      const { data: allPayments, error: paymentsError } = await supabase
        .from('payments')
        .select('*')
      
      if (profilesError || paymentsError) {
        throw new Error('Erreur chargement données admin')
      }
      
      const completedPayments = allPayments?.filter(p => p.status === 'completed') || []
      const totalRevenue = completedPayments.reduce((sum, p) => sum + p.amount, 0)
      const totalTransactions = completedPayments.length
      const totalUsers = allProfiles?.length || 0
      
      // Calculer les utilisateurs actifs (ayant fait au moins une transaction)
      const activeUsers = new Set(completedPayments.map(p => p.user_id)).size
      
      // Nouveaux utilisateurs aujourd'hui
      const today = new Date()
      today.setHours(0, 0, 0, 0)
      const newUsersToday = allProfiles?.filter(p => new Date(p.created_at) >= today).length || 0
      
      setStats({
        totalUsers,
        totalRevenue,
        totalTransactions,
        systemHealth: 99.9, // Calculé basé sur les métriques système
        activeUsers,
        newUsersToday
      })
      
    } catch (error) {
      console.error('Erreur chargement stats admin:', error)
      toast.error('Erreur lors du chargement des statistiques')
      
      // Initialiser avec des valeurs vides
      setStats({
        totalUsers: 0,
        totalRevenue: 0,
        totalTransactions: 0,
        systemHealth: 100,
        activeUsers: 0,
        newUsersToday: 0
      })
    } finally {
      setLoading(false)
    }
  }

  const userGrowthData = [
    { month: 'Jan', users: 120, revenue: 1200000 },
    { month: 'Fév', users: 180, revenue: 1800000 },
    { month: 'Mar', users: 250, revenue: 2500000 },
    { month: 'Avr', users: 320, revenue: 3200000 },
    { month: 'Mai', users: 420, revenue: 4200000 },
    { month: 'Jun', users: 580, revenue: 5800000 }
  ]

  const providerStats = [
    { name: 'Orange Money', value: 45, color: '#FF6B35' },
    { name: 'Wave', value: 30, color: '#4ECDC4' },
    { name: 'MTN MoMo', value: 20, color: '#FFD93D' },
    { name: 'Autres', value: 5, color: '#E74C3C' }
  ]

  const adminCards = [
    {
      title: 'Utilisateurs Totaux',
      value: stats.totalUsers.toLocaleString(),
      change: '+12.5%',
      trend: 'up',
      icon: Users,
      color: 'text-blue-600'
    },
    {
      title: 'Revenus Totaux',
      value: `${(stats.totalRevenue / 1000000).toFixed(1)}M XOF`,
      change: '+18.2%',
      trend: 'up',
      icon: DollarSign,
      color: 'text-green-600'
    },
    {
      title: 'Transactions',
      value: stats.totalTransactions.toLocaleString(),
      change: '+8.7%',
      trend: 'up',
      icon: Activity,
      color: 'text-purple-600'
    },
    {
      title: 'Santé Système',
      value: `${stats.systemHealth}%`,
      change: '+0.1%',
      trend: 'up',
      icon: Shield,
      color: 'text-orange-600'
    }
  ]

  if (loading) {
    return (
      <div className="p-6 max-w-7xl mx-auto">
        <div className="animate-pulse space-y-6">
          <div className="h-8 bg-gray-200 rounded w-1/3"></div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="h-32 bg-gray-200 rounded-lg"></div>
            ))}
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-8"
      >
        <h1 className="text-3xl font-bold text-gray-900 mb-2">
          Tableau de Bord Administrateur
        </h1>
        <p className="text-gray-600">
          Bienvenue {profile?.name}, voici un aperçu global de la plateforme PayFuse
        </p>
      </motion.div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        {adminCards.map((card, index) => (
          <motion.div
            key={card.title}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
            className="bg-white rounded-lg shadow-sm border p-6 hover:shadow-md transition-shadow"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">{card.title}</p>
                <p className="text-2xl font-bold text-gray-900 mt-2">{card.value}</p>
                <div className="flex items-center mt-2">
                  <TrendingUp className="w-4 h-4 text-green-600" />
                  <span className="text-sm ml-1 text-green-600">{card.change}</span>
                  <span className="text-xs text-gray-500 ml-1">vs mois dernier</span>
                </div>
              </div>
              <div className="p-3 rounded-lg bg-gray-50">
                <card.icon className={`w-6 h-6 ${card.color}`} />
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      <div className="grid lg:grid-cols-2 gap-6 mb-8">
        {/* Croissance Utilisateurs */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          className="bg-white rounded-lg shadow-sm border p-6"
        >
          <h2 className="text-lg font-semibold mb-4">Croissance des Utilisateurs</h2>
          <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={userGrowthData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="month" stroke="#6b7280" fontSize={12} />
                <YAxis stroke="#6b7280" fontSize={12} />
                <Tooltip 
                  formatter={(value, name) => [
                    name === 'revenue' ? `${value} XOF` : value,
                    name === 'revenue' ? 'Revenus' : 'Utilisateurs'
                  ]}
                  contentStyle={{ backgroundColor: '#fff', border: '1px solid #e5e7eb', borderRadius: '8px' }}
                />
                <Line 
                  type="monotone" 
                  dataKey="users" 
                  stroke="#3B82F6" 
                  strokeWidth={3}
                  dot={{ fill: '#3B82F6', strokeWidth: 2, r: 4 }}
                />
                <Line 
                  type="monotone" 
                  dataKey="revenue" 
                  stroke="#10B981" 
                  strokeWidth={3}
                  dot={{ fill: '#10B981', strokeWidth: 2, r: 4 }}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </motion.div>

        {/* Répartition Providers */}
        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          className="bg-white rounded-lg shadow-sm border p-6"
        >
          <h2 className="text-lg font-semibold mb-4">Utilisation des Providers</h2>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={providerStats}
                  cx="50%"
                  cy="50%"
                  outerRadius={80}
                  dataKey="value"
                  label={({ name, value }) => `${name}: ${value}%`}
                  labelLine={false}
                >
                  {providerStats.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip 
                  formatter={(value) => [`${value}%`, 'Utilisation']}
                  contentStyle={{ backgroundColor: '#fff', border: '1px solid #e5e7eb', borderRadius: '8px' }}
                />
              </PieChart>
            </ResponsiveContainer>
          </div>
          
          <div className="mt-4 space-y-2">
            {providerStats.map((provider) => (
              <div key={provider.name} className="flex justify-between items-center p-2 bg-gray-50 rounded">
                <div className="flex items-center">
                  <div 
                    className="w-3 h-3 rounded-full mr-2" 
                    style={{ backgroundColor: provider.color }}
                  />
                  <span className="text-sm font-medium">{provider.name}</span>
                </div>
                <span className="text-sm font-semibold">{provider.value}%</span>
              </div>
            ))}
          </div>
        </motion.div>
      </div>

      {/* Alertes Système */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
        className="bg-white rounded-lg shadow-sm border p-6"
      >
        <h2 className="text-lg font-semibold mb-4 flex items-center">
          <AlertTriangle className="w-5 h-5 mr-2 text-yellow-600" />
          Alertes Système
        </h2>
        
        <div className="space-y-3">
          <div className="flex items-center p-3 bg-green-50 border border-green-200 rounded-lg">
            <div className="w-2 h-2 bg-green-500 rounded-full mr-3"></div>
            <div className="flex-1">
              <p className="text-sm font-medium text-green-900">Tous les systèmes opérationnels</p>
              <p className="text-xs text-green-700">Dernière vérification: il y a 2 minutes</p>
            </div>
          </div>
          
          <div className="flex items-center p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
            <div className="w-2 h-2 bg-yellow-500 rounded-full mr-3"></div>
            <div className="flex-1">
              <p className="text-sm font-medium text-yellow-900">Maintenance programmée</p>
              <p className="text-xs text-yellow-700">Prévue pour dimanche 2h00 - 4h00</p>
            </div>
          </div>
        </div>
      </motion.div>

      {/* Actions Rapides Admin */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.4 }}
        className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-6"
      >
        <div className="bg-white rounded-lg shadow-sm border p-6 text-center">
          <Database className="w-8 h-8 text-blue-600 mx-auto mb-3" />
          <h3 className="font-semibold mb-2">Gestion Base de Données</h3>
          <p className="text-sm text-gray-600 mb-4">Administrer les données et sauvegardes</p>
          <button className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors">
            Accéder
          </button>
        </div>

        <div className="bg-white rounded-lg shadow-sm border p-6 text-center">
          <Server className="w-8 h-8 text-green-600 mx-auto mb-3" />
          <h3 className="font-semibold mb-2">Monitoring Serveurs</h3>
          <p className="text-sm text-gray-600 mb-4">Surveiller les performances système</p>
          <button className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors">
            Voir Détails
          </button>
        </div>

        <div className="bg-white rounded-lg shadow-sm border p-6 text-center">
          <Shield className="w-8 h-8 text-purple-600 mx-auto mb-3" />
          <h3 className="font-semibold mb-2">Sécurité & Logs</h3>
          <p className="text-sm text-gray-600 mb-4">Analyser les logs de sécurité</p>
          <button className="bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 transition-colors">
            Analyser
          </button>
        </div>
      </motion.div>
    </div>
  )
}

export default AdminDashboard